import { useEffect, useState, useRef, useMemo, useCallback, MutableRef } from 'preact/hooks';

// D3 and topojson.
import * as d3 from 'd3';
import * as d3GeoProjection from 'd3-geo-projection';
import * as topojson from 'topojson-client';

// Data.
import world from '../../data/countries-110m.json';

// Utils.
import mapSettings from './utils/mapSettings';
import { zoomToCountry, zoomIn, zoomOut, zoomReset } from './utils/zoom';

// Components.
import { ZoomControls } from './ZoomControls';
import { Countries, CountrySetting } from './Countries';

// Types.
import { SingleCountry } from './Country';

// Global map settings.
const { mapWidth, mapScaleMin, mapScaleMax, mapProjection } = mapSettings;

/**
 * Zoom transform hook.
 * Use zoom behavior to set map transform.
 *
 * @param svgRef - d3 svg reference
 * @return zoom, mapTransform
 */
const useZoomTransform = ( svgRef: MutableRef<Element|null> ): [d3.ZoomBehavior<Element, unknown>, d3.ZoomTransform] => {
	const [ mapTransform, setMapTransform ] = useState( d3.zoomIdentity );

	const zoomed = useCallback(
		( event: { transform: d3.ZoomTransform } ) => {
			const { transform } = event;
			setMapTransform( transform );
		}, []
	);

	const zoom = useMemo(
		() =>
			d3
				.zoom()
				.scaleExtent( [ mapScaleMin, mapScaleMax ] )
				.on( 'zoom', zoomed ),
		[ zoomed ]
	);

	useEffect( () => {
		const svg = d3.select( svgRef.current );
		svg.call( zoom );
	}, [ zoom, svgRef ] );

	return [ zoom, mapTransform ];
};

/**
 * Sphere object. Used to create spherical path for the earth.
 * Note that due to a typing error, the geometries property is null.
 * See https://github.com/d3/d3/discussions/3552.
 * And https://d3js.org/d3-geo/path#geoPath.
 */
const sphere = { type: 'Sphere', geometries: null };

// Props for WorldMap component.
export type WorldMapProps = {
	countrySettings: { [ key: number ]: CountrySetting };
	onZoomAction?: ( type: string ) => string|null|undefined;
	onCountryClick?: ( country: { country: SingleCountry; data: { [ key: string ]: any } } ) => void;
	countryDefaultAttributes: { [ key: string ]: string };
	focusedCountry: number;
};

/**
 * World Map.
 *
 * @param props                          - component props
 * @param props.countrySettings          - Object of country settings. Matched against country IDs to add data and apply attributes as needed including fill, classNames, etc.
 * @param props.onZoomAction             - callback function to run when the zoom control is clicked. Zoom "type" will be passed tothe function. The zoom behavior can be filtered/modified/disabled by returning a new type. Types are "zoomIn", "zoomOut", or "zoomReset".
 * @param props.onCountryClick           - callback function to run when a country is clicked. The country object and data object will be passed to the function.
 * @param props.countryDefaultAttributes - default attributes to apply to all countries such as fill, stroke, className, etc.
 *
 * @param props.focusedCountry           - The ISO number of the country to focus on.
 */
export default function WorldMap( { countrySettings, onZoomAction, onCountryClick, countryDefaultAttributes, focusedCountry }: WorldMapProps ) {
	// Create a ref for the svg element.
	const svgRef: MutableRef<SVGSVGElement> = useRef();

	// Setup map projection and path generator.
	const pathGenerator = useMemo( () => {
		const projectionFunction =
			d3[mapProjection] || d3GeoProjection[mapProjection];
		const projection = projectionFunction().fitWidth( mapWidth, sphere );
		return d3.geoPath( projection );
	}, [] );

	// Set svg height to map's height.
	const [
		[ x0, y0 ], // eslint-disable-line no-unused-vars
		[ x1, y1 ], // eslint-disable-line no-unused-vars
	] = pathGenerator.bounds( sphere );

	const mapHeight = y1;

	// Get the countries from topojson data.
	const countries = useMemo(
		() => topojson.feature( world, world.objects.countries ),
		[]
	);

	// Add path data to countries.
	const countriesWithPathData = useMemo(
		() =>
			countries.features.map( ( country ) => (
				{
					...country,
					d: pathGenerator( country ),
				}
			) ),

		[ countries, pathGenerator ]
	);

	// Get zoom function and transform object.
	const [ zoom, mapTransform ] = useZoomTransform( svgRef );

	/**
	 * Zoom to country callback.
	 * Zoom to the country when clicked.
	 *
	 * @param {Object} countryObj - country object.
	 */
	const countryZoomCallback = useCallback(
		( countryObj ) => zoomToCountry( svgRef, zoom, pathGenerator, countryObj, mapHeight ),
		[ zoom, pathGenerator, mapHeight ]
	);

	/**
	 * Zoom callback for zoom controls.
	 *
	 * @param {string} type - zoom action type.
	 */
	const zoomCallback = useCallback(
		( type: string ) => {
			// Fire the onZoomAction callback if it exists and use its returned type or fall back to original type if null.
			const filteredType: string = onZoomAction ? onZoomAction( type ) ?? type : type;

			switch ( filteredType ) {
				case 'zoomIn':
					zoomIn( svgRef, zoom );
					break;
				case 'zoomOut':
					zoomOut( svgRef, zoom );
					break;
				case 'zoomReset':
					zoomReset( svgRef, zoom );
					break;
				default:
					break;
			}
		},
		[ zoom, onZoomAction ]
	);

	// Handle focused country change/zoom.
	useEffect( () => {
		if ( ! svgRef.current ) {
			return;
		}

		const countryObj: SingleCountry = countries.features.find( ( country: SingleCountry ) => country.id === focusedCountry );

		if ( countryObj ) {
			countryZoomCallback( countryObj );
		} else {
			zoomCallback( 'zoomReset' );
		}
	}, [ countries, countryZoomCallback, focusedCountry, zoomCallback ] );

	return (
		<>
			<ZoomControls zoomCallback={ zoomCallback } />

			<svg ref={ svgRef } viewBox={ `0 0 ${ mapWidth } ${ mapHeight }` }>
				<defs>
					{ /* some projections bleed outside the edges of the Earth's sphere */ }
					{ /* let's create a clip path to keep things in bounds */ }
					<clipPath id="Map__sphere">
						<path d={ pathGenerator( sphere ) } />
					</clipPath>
				</defs>

				<g transform={ mapTransform?.toString() ?? '' }>
					<g style={ { clipPath: 'url(#Map__sphere)' } }>
						<Countries
							countries={ countriesWithPathData }
							countrySettings={ countrySettings }
							onCountryClick={ onCountryClick }
							countryDefaultAttributes={ countryDefaultAttributes } />
					</g>
				</g>
			</svg>
		</>
	);
}
